import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Calendar, 
  ChevronLeft, 
  ChevronRight,
  CheckCircle2,
  Droplets,
  Heart,
  ArrowLeft
} from "lucide-react";
import { getProgressByDateRange, getWaterByDateRange, getSymptomsByDateRange } from "./partnerApi";

export default function HistoryView({ userId, onBack }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [monthData, setMonthData] = useState({});
  const [waterData, setWaterData] = useState({});
  const [symptomData, setSymptomData] = useState({});

  const fetchMonthData = useCallback(async () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const startDate = new Date(year, month, 1).toISOString().split('T')[0];
    const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];

    const [progress, water, symptoms] = await Promise.all([
      getProgressByDateRange(userId, startDate, endDate),
      getWaterByDateRange(userId, startDate, endDate),
      getSymptomsByDateRange(userId, startDate, endDate)
    ]);

    const progressMap = {};
    progress?.forEach(p => { progressMap[p.date] = p.data; });
    setMonthData(progressMap);

    const waterMap = {};
    water?.forEach(w => { waterMap[w.date] = w.cups; });
    setWaterData(waterMap);

    const symptomMap = {};
    symptoms?.forEach(s => { symptomMap[s.date] = s.data; });
    setSymptomData(symptomMap);
  }, [userId, currentDate]);

  useEffect(() => {
    fetchMonthData();
  }, [fetchMonthData]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="aspect-square" />);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = monthData[dateStr];
      const water = waterData[dateStr] || 0;
      const hasData = dayData || water > 0;
      const completedCount = dayData ? Object.values(dayData).filter(Boolean).length : 0;
      const isSelected = selectedDate === dateStr;
      const isToday = dateStr === new Date().toISOString().split('T')[0];

      days.push(
        <button
          key={day}
          onClick={() => setSelectedDate(dateStr)}
          className={`aspect-square rounded-2xl flex flex-col items-center justify-center relative transition-all ${
            isSelected 
              ? 'bg-rose-500 text-white scale-105 shadow-lg' 
              : hasData 
                ? 'bg-gradient-to-br from-rose-100 to-pink-100 hover:from-rose-200 hover:to-pink-200' 
                : 'hover:bg-slate-100'
          } ${isToday && !isSelected ? 'ring-2 ring-rose-400' : ''}`}
        >
          <span className="text-sm font-semibold">{day}</span>
          {hasData && !isSelected && (
            <div className="flex gap-0.5 mt-1">
              {completedCount > 0 && (
                <div className="w-1.5 h-1.5 rounded-full bg-rose-400" />
              )}
              {water > 0 && (
                <div className="w-1.5 h-1.5 rounded-full bg-sky-400" />
              )}
            </div>
          )}
        </button>
      );
    }

    return days;
  };

  const renderSelectedDayDetails = () => {
    if (!selectedDate) return null;

    const dayData = monthData[selectedDate] || {};
    const water = waterData[selectedDate] || 0;
    const symptoms = symptomData[selectedDate] || {};

    const questItems = [
      { key: 'breakfast', label: 'Nourishing Breakfast', icon: '🍳' },
      { key: 'movement', label: 'Movement Quest', icon: '🏃‍♀️' },
      { key: 'water', label: 'Hydration Hero', icon: '💧' },
      { key: 'supplements', label: 'Supplement Champion', icon: '💊' },
      { key: 'sleep', label: 'Sleep Guardian', icon: '😴' },
      { key: 'mindfulness', label: 'Mindful Moment', icon: '🧘‍♀️' },
    ];

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-4 p-4 bg-white rounded-3xl border border-rose-100 shadow-lg"
      >
        <h3 className="text-lg font-bold text-slate-800 mb-3">
          {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </h3>

        {/* Quests */}
        <div className="space-y-2 mb-4">
          {questItems.map(item => (
            <div key={item.key} className="flex items-center gap-2">
              <span className="text-lg">{item.icon}</span>
              <span className={`text-sm ${dayData[item.key] ? 'text-slate-800' : 'text-slate-400'}`}>
                {item.label}
              </span>
              {dayData[item.key] && (
                <CheckCircle2 className="w-4 h-4 text-emerald-500 ml-auto" />
              )}
            </div>
          ))}
        </div>

        {/* Water */}
        <div className="flex items-center gap-2 mb-4 p-3 bg-sky-50 rounded-2xl">
          <Droplets className="w-5 h-5 text-sky-500" />
          <span className="text-sm text-slate-700">Water: {water} cups</span>
        </div>

        {/* Symptoms */}
        {Object.keys(symptoms).length > 0 && (
          <div className="p-3 bg-purple-50 rounded-2xl">
            <p className="text-sm font-medium text-purple-700 mb-2">How You Felt</p>
            <div className="flex flex-wrap gap-2">
              {symptoms.mood && (
                <span className="px-2 py-1 bg-white rounded-full text-xs">Mood: {symptoms.mood}/5</span>
              )}
              {symptoms.energy && (
                <span className="px-2 py-1 bg-white rounded-full text-xs">Energy: {symptoms.energy}/5</span>
              )}
              {symptoms.pain && (
                <span className="px-2 py-1 bg-white rounded-full text-xs">Pain: {symptoms.pain}/5</span>
              )}
            </div>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-100 via-pink-50 to-orange-100 p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={onBack}
            className="p-2 rounded-full bg-white shadow-sm border border-rose-200 hover:bg-rose-50 transition"
          >
            <ArrowLeft className="w-5 h-5 text-rose-600" />
          </button>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-rose-500" />
            Your Journey
          </h1>
        </div>

        {/* Calendar */}
        <div className="bg-white rounded-3xl p-4 shadow-lg border border-rose-100">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={prevMonth}
              className="p-2 rounded-full hover:bg-slate-100 transition"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <h2 className="text-lg font-bold text-slate-800">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <button
              onClick={nextMonth}
              className="p-2 rounded-full hover:bg-slate-100 transition"
            >
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          {/* Day Names */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-xs font-medium text-slate-500 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {renderCalendar()}
          </div>

          {/* Legend */}
          <div className="flex gap-4 mt-4 text-xs text-slate-600">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-rose-400" />
              <span>Quests</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-sky-400" />
              <span>Water</span>
            </div>
          </div>
        </div>

        {/* Selected Day Details */}
        <AnimatePresence>
          {selectedDate && renderSelectedDayDetails()}
        </AnimatePresence>

        {/* Encouragement */}
        <div className="mt-6 p-4 bg-gradient-to-r from-rose-200 to-pink-200 rounded-3xl text-center">
          <Heart className="w-6 h-6 text-rose-500 mx-auto mb-2" />
          <p className="text-sm text-slate-700">
            Every step forward is progress. You're doing amazing! 💕
          </p>
        </div>
      </div>
    </div>
  );
}